/**
 * G1 — production OIDC cookie-land mount orchestration (CSP caller).
 *
 * Runs {@link completeOidcCookieLand} (strip + {@link OIDC_LAND_QUERY_CHANGED_EVENT}
 * on success/mismatch) with H1 in-tab soft retries and R4 post-give-up definitive
 * force-strip so `?oidcLand=1` never sticks forever.
 *
 * Caller must wrap with {@link beginLoginLand}/{@link endLoginLand} so boot `/me`
 * waits and land’s `forLoginLand` probe can wire.
 */

import {
  clearOidcLandPostGiveUpBudgetState,
  completeOidcCookieLand,
  getOidcLandPostGiveUpBudgetState,
  hasOidcLandQuery,
  noteOidcLandAggregateStart,
  OIDC_LAND_INTAB_RETRY_ATTEMPTS,
  OIDC_LAND_INTAB_RETRY_INTERVAL_MS,
  OIDC_LAND_POST_GIVEUP_TRANSIENT_ATTEMPTS,
  setOidcLandPostGiveUpBudgetState,
  stripOidcLandQueryFromLocation,
  tryAcquireVisibilityOidcLandSlot,
  type CompleteOidcCookieLandResult,
} from './oidcCookieLand.js';
import { handoffOidcPopupToOpener } from './oidcPopupBridge.js';

export interface RunOidcCookieLandMountInput {
  readonly tenantCode: string;
  readonly apiBaseUrl: string;
  readonly isCancelled?: () => boolean;
}

export interface RunOidcCookieLandMountResult {
  readonly handled: boolean;
}

function waitMs(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

async function landOnce(
  input: RunOidcCookieLandMountInput,
  softAuthRejectBudgetRemaining: number
): Promise<CompleteOidcCookieLandResult> {
  return completeOidcCookieLand({
    tenantCode: input.tenantCode,
    apiBaseUrl: input.apiBaseUrl,
    softAuthRejectBudgetRemaining,
  });
}

/**
 * Single-shot mount path: soft H1 retries → R4 definitive → force-strip if needed.
 * Returns handled=true when credentials `/me` matched and land stripped.
 */
export async function runOidcCookieLandMount(
  input: RunOidcCookieLandMountInput
): Promise<RunOidcCookieLandMountResult> {
  if (typeof window === 'undefined' || !hasOidcLandQuery(window.location.search)) {
    return { handled: false };
  }

  noteOidcLandAggregateStart();

  // Residual named popup child (legacy): hand land URL to opener if present.
  if (handoffOidcPopupToOpener(window.location.href, true)) {
    return { handled: true };
  }

  const cancelled = (): boolean => input.isCancelled?.() === true;

  // H2 — remount must not restart soft budget if give-up already recorded.
  const prior = getOidcLandPostGiveUpBudgetState();
  let softExhausted = prior.softAuthBudgetExhausted;

  let result: CompleteOidcCookieLandResult = {
    handled: false,
    wroteJwtTenantCode: false,
    claimed: false,
  };

  if (!softExhausted) {
    // Fresh soft start — clear stale post-give-up count from a prior URL land.
    clearOidcLandPostGiveUpBudgetState();
    result = await landOnce(input, OIDC_LAND_INTAB_RETRY_ATTEMPTS);
    if (cancelled()) {
      return { handled: result.handled };
    }

    for (
      let attempt = 0;
      attempt < OIDC_LAND_INTAB_RETRY_ATTEMPTS &&
      !result.handled &&
      hasOidcLandQuery(window.location.search);
      attempt += 1
    ) {
      await waitMs(OIDC_LAND_INTAB_RETRY_INTERVAL_MS);
      if (cancelled()) {
        return { handled: result.handled };
      }
      if (!hasOidcLandQuery(window.location.search)) {
        break;
      }
      const isLast = attempt === OIDC_LAND_INTAB_RETRY_ATTEMPTS - 1;
      result = await landOnce(input, isLast ? 0 : 1);
      if (cancelled()) {
        return { handled: result.handled };
      }
      if (result.handled) {
        break;
      }
    }

    if (!result.handled && hasOidcLandQuery(window.location.search)) {
      softExhausted = true;
      setOidcLandPostGiveUpBudgetState({ softAuthBudgetExhausted: true });
    }
  }

  // R4 — one immediate definitive (budget 0) after soft give-up.
  if (
    !result.handled &&
    softExhausted &&
    hasOidcLandQuery(window.location.search)
  ) {
    result = await landOnce(input, 0);
    if (cancelled()) {
      return { handled: result.handled };
    }
    if (!result.handled && hasOidcLandQuery(window.location.search)) {
      const nextCount =
        getOidcLandPostGiveUpBudgetState().postGiveUpTransientCount + 1;
      setOidcLandPostGiveUpBudgetState({
        softAuthBudgetExhausted: true,
        postGiveUpTransientCount: nextCount,
      });
      if (nextCount >= OIDC_LAND_POST_GIVEUP_TRANSIENT_ATTEMPTS) {
        // Perpetual transient — force-strip so guards / restore can proceed.
        stripOidcLandQueryFromLocation();
      }
    }
  }

  return { handled: result.handled };
}

/**
 * H3 — visibility/pageshow bonus land while `?oidcLand=1` remains.
 * Uses definitive budget 0 after soft give-up; soft budget 1 otherwise.
 */
export async function runOidcCookieLandVisibilityBonus(
  input: RunOidcCookieLandMountInput
): Promise<RunOidcCookieLandMountResult> {
  if (typeof window === 'undefined' || !hasOidcLandQuery(window.location.search)) {
    return { handled: false };
  }
  if (!tryAcquireVisibilityOidcLandSlot()) {
    return { handled: false };
  }
  const softExhausted =
    getOidcLandPostGiveUpBudgetState().softAuthBudgetExhausted;
  const result = await landOnce(input, softExhausted ? 0 : 1);
  if (
    !result.handled &&
    softExhausted &&
    hasOidcLandQuery(window.location.search)
  ) {
    const nextCount =
      getOidcLandPostGiveUpBudgetState().postGiveUpTransientCount + 1;
    setOidcLandPostGiveUpBudgetState({
      softAuthBudgetExhausted: true,
      postGiveUpTransientCount: nextCount,
    });
    if (nextCount >= OIDC_LAND_POST_GIVEUP_TRANSIENT_ATTEMPTS) {
      stripOidcLandQueryFromLocation();
    }
  }
  return { handled: result.handled };
}
